import type { LoquixTranslations } from '../types.js';

/**
 * Default English translations — the reference locale.
 * Every key in LoquixTranslations must have an entry here.
 */
export const en: LoquixTranslations = {
  // === loquix-action-copy ===
  'actionCopy.label': 'Copy message',
  'actionCopy.copied': 'Copied',

  // === loquix-action-edit ===
  'actionEdit.triggerLabel': 'Edit message',
  'actionEdit.textareaLabel': 'Edit message',
  'actionEdit.submitLabel': 'Save & Submit',
  'actionEdit.cancelLabel': 'Cancel',
  'actionEdit.editingBadge': 'Editing\u2026',

  // === loquix-action-feedback ===
  'actionFeedback.positiveLabel': 'Good response',
  'actionFeedback.negativeLabel': 'Bad response',

  // === loquix-message-actions ===
  'messageActions.toolbarLabel': 'Message actions',
  'messageActions.regenerate': 'Regenerate response',

  // === loquix-chat-composer ===
  'chatComposer.placeholder': 'Type a message...',
  'chatComposer.sendLabel': 'Send message',
  'chatComposer.stopLabel': 'Stop generation',

  // === loquix-prompt-input ===
  'promptInput.placeholder': 'Type a message...',

  // === loquix-message-item ===
  'messageItem.showMore': 'Show more',
  'messageItem.showLess': 'Show less',
  'messageItem.errorText': 'Failed to generate response',
  'messageItem.retryLabel': 'Retry',
  'messageItem.userAvatar': 'You',

  // === loquix-generation-controls ===
  'generationControls.toolbarLabel': 'Generation controls',
  'generationControls.stop': 'Stop',
  'generationControls.stopLabel': 'Stop generation',
  'generationControls.pause': 'Pause',
  'generationControls.pauseLabel': 'Pause generation',
  'generationControls.resume': 'Resume',
  'generationControls.resumeLabel': 'Resume generation',

  // === loquix-typing-indicator ===
  'typingIndicator.ariaLabel': 'AI is typing',
  'typingIndicator.thinkingMessage': 'Thinking\u2026',
  'typingIndicator.workingMessage': 'Working\u2026',

  // === loquix-template-picker ===
  'templatePicker.heading': 'Choose a template',
  'templatePicker.closeLabel': 'Close',
  'templatePicker.searchPlaceholder': 'Search templates...',
  'templatePicker.searchLabel': 'Search templates',
  'templatePicker.emptyText': 'No templates match your search.',

  // === loquix-model-selector ===
  'modelSelector.placeholder': 'Select model...',
  'modelSelector.searchPlaceholder': 'Search models...',
  'modelSelector.searchLabel': 'Search models',
  'modelSelector.emptyText': 'No models found',

  // === loquix-attachment-chip ===
  'attachmentChip.pending': 'Waiting\u2026',
  'attachmentChip.connecting': 'Connecting\u2026',
  'attachmentChip.error': 'Error',
  'attachmentChip.uploadError': 'Upload error',
  'attachmentChip.retryLabel': 'Retry upload {filename}',
  'attachmentChip.removeLabel': 'Remove {filename}',

  // === loquix-attachment-panel ===
  'attachmentPanel.triggerLabel': 'Add files',
  'attachmentPanel.dropOverlay': 'Drop files here',

  // === loquix-message-attachments ===
  'messageAttachments.removeLabel': 'Remove {filename}',
  'messageAttachments.moreCount': '+{count} more',

  // === loquix-suggestion-chips ===
  'suggestionChips.ariaLabel': 'Suggestions',
  'suggestionChips.moreCount': '+{count} more',

  // === loquix-nudge-banner ===
  'nudgeBanner.dismissLabel': 'Dismiss',

  // === loquix-dropdown-select ===
  'dropdownSelect.placeholder': 'Select...',
  'dropdownSelect.searchPlaceholder': 'Search...',

  // === loquix-filter-bar ===
  'filterBar.ariaLabel': 'Filters',
  'filterBar.excludeLabel': 'Exclude:',
  'filterBar.excludePlaceholder': 'Topics to avoid...',

  // === loquix-mode-selector ===
  'modeSelector.ariaLabel': 'Chat mode',
  'modeSelector.selectLabel': 'Select mode',

  // === loquix-parameter-panel ===
  'parameterPanel.showAdvanced': 'Show advanced',
  'parameterPanel.hideAdvanced': 'Hide advanced',

  // === loquix-drop-zone ===
  'dropZone.label': 'Drop files to upload',

  // === loquix-welcome-screen ===
  'welcomeScreen.heading': 'How can I help you?',

  // === loquix-caveat-notice ===
  'caveatNotice.message': 'AI can make mistakes. Check important info.',

  // === loquix-chat-header ===
  'chatHeader.agentName': 'AI Assistant',

  // === loquix-disclosure-badge ===
  'disclosureBadge.generated': 'Generated by {agent}',
  'disclosureBadge.summarized': 'Summarized by {agent}',
  'disclosureBadge.rewrote': 'Rewritten by {agent}',
  'disclosureBadge.suggested': 'Suggested by {agent}',

  // === loquix-message-avatar ===
  'messageAvatar.altFallback': 'Avatar',
  'messageAvatar.namedAlt': '{name} avatar',
  'messageAvatar.aiAlt': 'AI avatar',

  // === loquix-scroll-anchor ===
  'scrollAnchor.label': 'Scroll to bottom',

  // === loquix-confidence-indicator (Phase 4 PR #2) ===
  'confidenceIndicator.labelDefault': 'Confidence',
  'confidenceIndicator.levelLow': 'low',
  'confidenceIndicator.levelMedium': 'medium',
  'confidenceIndicator.levelHigh': 'high',

  // === loquix-uncertainty-marker (Phase 4 PR #2) ===
  'uncertaintyMarker.tipUnsure': 'Model is uncertain about this claim',
  'uncertaintyMarker.tipNeedsVerification': 'This statement needs verification',
  'uncertaintyMarker.tipSpeculative': 'Speculative — not directly grounded in sources',

  // === loquix-feedback-form (Phase 4 PR #2) ===
  'feedbackForm.groupLabel': 'Rate this response',
  'feedbackForm.reasonsGroupLabel': 'Reason',
  'feedbackForm.helpfulLabel': 'Helpful',
  'feedbackForm.notHelpfulLabel': 'Not helpful',
  'feedbackForm.sendLabel': 'Send',
  'feedbackForm.cancelLabel': 'Cancel',
  'feedbackForm.placeholderUp': 'What did you like? (optional)',
  'feedbackForm.placeholderDown': 'What went wrong? (optional)',
  'feedbackForm.thanksMessage': 'Thanks — your feedback was sent.',
  'feedbackForm.reasonAccurate': 'Accurate',
  'feedbackForm.reasonWellWritten': 'Well-written',
  'feedbackForm.reasonHelpful': 'Helpful',
  'feedbackForm.reasonInaccurate': 'Inaccurate',
  'feedbackForm.reasonOffTopic': 'Off-topic',
  'feedbackForm.reasonUnsafe': 'Unsafe',
  'feedbackForm.reasonOther': 'Other',

  // === loquix-correction-input (Phase 4 PR #2) ===
  'correctionInput.originalLabel': 'Original',
  'correctionInput.correctionLabel': 'Correction',
  'correctionInput.reasonLabel': 'Why is this wrong?',
  'correctionInput.reasonPlaceholder': 'Brief explanation',
  'correctionInput.correctionPlaceholder': 'Provide the correct version…',
  'correctionInput.submitLabel': 'Submit correction',
  'correctionInput.cancelLabel': 'Cancel',
  'correctionInput.reasonRequiredMark': '*',

  // === loquix-disagreement-marker (Phase 4 PR #2) ===
  'disagreementMarker.title': 'User disagreed with this response',
  'disagreementMarker.markResolvedLabel': 'Mark resolved',
  'disagreementMarker.inlineLabel': 'Disagreement',
};
